// src/FrameSim.jsx
import React, {
  useContext,
  useMemo,
  useEffect,
  Suspense,
  useState,
  useRef,
} from "react";
import { Canvas, useThree, useLoader } from "@react-three/fiber";
import { TextureLoader } from "three/src/loaders/TextureLoader";
import { ControlContext } from "./ControlContext.jsx";
import Print from "./FrameTypes/FrameComp/Print.jsx";
import BorderSwitch from "./FrameTypes/FrameComp/BorderSwitch.jsx";
import Scene from "./Scene";
import CameraManager from "./CameraManager";
import * as THREE from "three";
import { SoftShadows, Preload } from "@react-three/drei";
import { texturePreloader } from "./utils/TexturePreloader";
import { withCrossOrigin } from "./utils/ensureTextureCrossOrigin";
import LoadingScreen from "./components/LoadingScreen";
import MeasurementsProbe from "./components/MeasurementsProbe";
import MeasurementsGizmo from "./components/MeasurementsGizmo";

import BoxFrame from "./FrameTypes/BoxFrame";
import AluminumFrame from "./FrameTypes/AluminumFrame";
import FloatFrame from "./FrameTypes/FloatFrame";
import FaceMount from "./FrameTypes/FaceMount";
import CanvasFrame from "./FrameTypes/CanvasFrame";
import PrintOnly from "./FrameTypes/PrintOnly";

function SceneConfigurer() {
  const { gl } = useThree();
  const { sceneToneMapping, sceneExposure } = useContext(ControlContext);
  useEffect(() => {
    gl.toneMapping = sceneToneMapping;
    gl.toneMappingExposure = sceneExposure;
  }, [gl, sceneToneMapping, sceneExposure]);
  return null;
}

const EarlyFrameDisplay = () => {
  const {
    printWidth,
    printHeight,
    borderWidth,
    borderHeight,
    borderColor,
    wallColor,
    paperMaterial,
  } = useContext(ControlContext);

  const shadowMap = useLoader(
    TextureLoader,
    "/models/WALL copy3.webp",
    withCrossOrigin
  );

  return (
    <>
      {/* Simple wall */}
      <mesh position={[0, -20, 0]} receiveShadow>
        <planeGeometry args={[500, 350]} />
        <meshBasicMaterial color={wallColor} map={shadowMap} />
      </mesh>

      {/* Simple border */}
      {borderWidth > 0 && borderHeight > 0 && (
        <mesh position={[0, 0, 0]} receiveShadow>
          <planeGeometry
            args={[
              printWidth + 2 * borderWidth,
              printHeight + 2 * borderHeight,
            ]}
          />
          <meshBasicMaterial color={borderColor || "#FFFFFF"} />
        </mesh>
      )}

      {/* Simple print */}
      <mesh position={[0, 0, 0.2]}>
        <planeGeometry args={[printWidth, printHeight]} />
        <meshBasicMaterial map={paperMaterial[0].map} />
      </mesh>

      <SoftShadows size={5} near={9.5} samples={16} focus={2} />
      <directionalLight
        position={[-80, 65, 80]}
        intensity={1.8}
        castShadow
        shadow-camera-top={350}
        shadow-camera-bottom={-350}
        shadow-camera-right={350}
        shadow-camera-left={-350}
        shadow-camera-near={0}
        shadow-mapSize={[1024, 1024]}
      />
      <directionalLight
        position={[80, -80, 160]}
        intensity={1.2}
        shadow-camera-top={250}
        shadow-camera-bottom={-250}
        shadow-camera-right={250}
        shadow-camera-left={-250}
        shadow-camera-near={0}
        shadow-mapSize={[1024, 1024]}
      />
    </>
  );
};

export default function FrameSim() {
  const { frameType, cameraZ, faceMountFrame, cameraType, boundingMesh } =
    useContext(ControlContext);
  const [primaryContentLoaded, setPrimaryContentLoaded] = useState(false);
  const [texturesLoaded, setTexturesLoaded] = useState(false);
  const [secondaryContentLoaded, setSecondaryContentLoaded] = useState(false);
  const [showingFallback, setShowingFallback] = useState(true);
  // Shared between the in-canvas projector and the 2D overlay. Mutated every
  // frame, deliberately outside React state.
  const measurementsRef = useRef({ axes: [], corners: [], onUpdate: null });

  // Move texture preloading to start only after primary content is loaded
  useEffect(() => {
    if (primaryContentLoaded) {
      const loadTextures = async () => {
        try {
          await texturePreloader.preloadAllTextures();
          setTexturesLoaded(true);
        } catch (error) {
          console.error("Error preloading textures:", error);
        }
      };
      loadTextures();
    }
  }, [primaryContentLoaded]);

  // Only dispatch ready event when everything is loaded
  useEffect(() => {
    if (texturesLoaded && primaryContentLoaded && secondaryContentLoaded) {
      window.dispatchEvent(new Event("control-provider-ready"));
    }
  }, [texturesLoaded, primaryContentLoaded, secondaryContentLoaded]);

  // Render appropriate frame based on type
  const renderFrame = useMemo(() => {
    switch (frameType) {
      case "Print":
        return <PrintOnly />;
      case "Box":
        return <BoxFrame />;
      case "Float":
        return <FloatFrame />;
      case "Aluminum":
        return <AluminumFrame />;
      case "Face Mount":
        return <FaceMount />;
      case "Canvas":
        return <CanvasFrame />;
      default:
        return <PrintOnly />;
    }
  }, [frameType, faceMountFrame]);

  return (
    <div className="canvas">
      <Canvas
        gl={{
          antialias: true,
          preserveDrawingBuffer: true,
          outputColorSpace: THREE.SRGBColorSpace,
          alpha: true,
        }}
        shadows
        camera={{
          position: [0, 0, cameraZ],
          fov: 30,
          near: 1,
          far: 1300,
        }}
      >
        <SceneConfigurer />
        <CameraManager cameraType={cameraType} />
        <MeasurementsProbe targetRef={measurementsRef} />

        <Suspense
          fallback={
            <>
              <EarlyFrameDisplay />
              <OnComplete onComplete={() => setShowingFallback(true)} />
            </>
          }
        >
          <Print />
          <BorderSwitch />
          <Scene />
          <OnComplete
            onComplete={() => {
              setPrimaryContentLoaded(true);
              setShowingFallback(false);
            }}
          />
        </Suspense>

        {primaryContentLoaded && (
          <Suspense fallback={null}>
            {renderFrame}
            <Preload all />
            <OnComplete
              onComplete={() => {
                setSecondaryContentLoaded(true);
              }}
            />
          </Suspense>
        )}
      </Canvas>
      <MeasurementsGizmo targetRef={measurementsRef} />
      <LoadingScreen isVisible={showingFallback} />
    </div>
  );
}

// Helper component to trigger callback when Suspense content is loaded
function OnComplete({ onComplete }) {
  useEffect(() => {
    onComplete();
  }, [onComplete]);
  return null;
}
